<template>
  <div class="plan-wrapper">
    <!--Info-->
    <div class="info-wrapper">
      <h3>{{ content.title }}</h3>
      <p>{{ content.description }}</p>
    </div>

    <!--Active plan-->
    <div v-if="!lodash.isEmpty(chargebeeSubscription)" class="active-plan">
      <!--Details-->
      <div class="left-column">
        <div class="title-wrapper">
          <h3 class="title">{{ plan.title }}</h3>
          <span
            class="table-tag"
            :class="subscription.status === 'active' ? 'green' : 'red'"
            >{{ subscription.status === "active" ? "Active" : "Expired" }}</span
          >
        </div>
        <p class="description">{{ plan.description }}</p>

        <!--Button-->
        <button
          class="btn btn-small btn-border"
          @click="confirmSubscriptionChange"
        >
          <span>Manage Subscription & Licenses</span>
        </button>
      </div>

      <!--Pricing-->
      <div class="right-column">
        <div class="plan-price">
          <span class="plan-currency">{{ planCurrency }}</span>
          <span class="plan-amount">{{ planTotalAmount }}</span>
          <span class="plan-duration">{{ planDuration }}</span>
        </div>

        <!--Plan Terms-->
        <div class="term-details">
          <span>{{
            moment
              .unix(chargebeeSubscription.current_term_start)
              .format("D MMM YYYY")
          }}</span>
          to
          <span>{{
            moment
              .unix(chargebeeSubscription.current_term_end)
              .format("D MMM YYYY")
          }}</span>
        </div>

        <!--Plan unit pricing-->
        <div class="plan-unit-pricing">
          {{ planCurrency }}
          {{ planUnitPrice }}
          / {{ plan.unit }}
          {{ planDuration }}
          for
          <em>{{ planUnitQuantity }} {{ plan.unit }}</em>
        </div>
      </div>
    </div>

    <!--Pre Loader-->
    <PreLoader v-else :show="true" class="pre-loader"></PreLoader>

    <!--Confirmation Modal-->
    <ConfirmationModal
      :fields="confirmationFields"
      @primaryEvent="manageSubscription"
      @secondaryEvent="closeModal"
    ></ConfirmationModal>
    <div
      class="bg-overlay light"
      :class="{ show: confirmationFields.show }"
      @click="closeModal"
    ></div>
  </div>
</template>

<script>
// Libraries
import _ from "lodash";

// Components
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import PreLoader from "@/components/loaders/PreLoader";

export default {
  name: "SubscribedPlan",
  props: {
    content: Object,
    plan: Object,
    chargebeeSubscription: Object,
  },
  components: { ConfirmationModal, PreLoader },
  data() {
    return {
      lodash: _,

      confirmationFields: {
        show: false,
        title: "License changes may affect agent access",
        type: "warning",
        modalType: "fixed",
        message:
          "If you reduce licenses, the most recently added agents will automatically lose access.",
        iconType: "exclamation",
        confirmAction: true,
        confirmValue: null,
        primaryBtn: {
          title: "Okay, manage subscription",
        },
        secondaryBtn: {
          title: "Cancel",
        },
      },
    };
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
    subscription() {
      return this.selectedApp.subscription;
    },

    // Get the plan currency
    planCurrency() {
      return this.getCurrency(this.chargebeeSubscription.currency_code);
    },

    // Total billing amount
    planTotalAmount() {
      const subscription = this.chargebeeSubscription.subscription_items[0];
      return subscription.amount / 100;
    },

    // Get the plan frequency
    planDuration() {
      const frequency = this.chargebeeSubscription.billing_period_unit;
      return `/ ${frequency}`;
    },

    // Get the plan unit price
    planUnitPrice() {
      const subscription = this.chargebeeSubscription.subscription_items[0];
      return subscription.unit_price / 100;
    },

    // Get the plan unit quantity
    planUnitQuantity() {
      const subscription = this.chargebeeSubscription.subscription_items[0];
      return subscription.quantity;
    },

    // Show the billable agents
  },
  created() {},
  methods: {
    // Confirm management
    confirmSubscriptionChange() {
      this.confirmationFields.show = true;
    },

    // Manage subscription
    manageSubscription() {
      this.$emit("manageSubscription");
      this.closeModal();
    },

    closeModal() {
      this.confirmationFields.show = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.plan-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  .active-plan,
  .info-wrapper {
    flex: 1;
  }

  .pre-loader {
    flex: 1;
    margin-left: 15%;
  }

  ::v-deep {
    .buffer-category {
      display: none;
    }
  }
}

.info-wrapper {
  h3 {
    font-family: $titleFont;
    font-size: $mediumFontSize;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: $smallFontSize;
    opacity: $lightOpacity;
  }
}

.active-plan {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid #ececec;

  .right-column {
    text-align: right;
  }

  .title-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;

    h3 {
      font-family: $titleFont;
      font-size: $largeFontSize;
      font-weight: $boldFontWeight;
      margin-bottom: 0;
    }
  }

  .description {
    font-size: $smallerFontSize;
    opacity: 0.75;
    margin: 0.25rem 0 1rem;
    line-height: 1.4;
  }

  .info-wrapper {
    margin-bottom: 0;
  }

  .details-title {
    display: block;
    color: $greyColor;
    font-size: $smallFontSize;
    margin-bottom: 0.5rem;
  }

  .plan-details {
    .plan-name {
      font-size: 2.75rem;
      color: $purpleColor;
      font-weight: 600;
    }
  }

  .term-details {
    color: $paragraphColor;
    font-size: $smallerFontSize;

    span {
      font-size: $smallFontSize;
      font-weight: 600;
    }
  }
}

.plan-pricing {
  text-align: right;
  margin-left: 3rem;
  margin-top: -0.5rem;
}

.plan-currency {
  color: $blackColor;
  font-weight: $boldFontWeight;
  font-size: $largeFontSize;
}

.plan-amount {
  color: $blackColor;
  font-weight: $boldFontWeight;
  font-size: $largestFontSize;
}

.plan-duration {
  color: lighten($greyColor, 15%);
  font-weight: $mediumFontWeight;
  font-size: $normalFontSize;
  margin-left: 0.45rem;
}

.plan-unit-pricing {
  font-size: $smallFontSize;
  font-weight: $mediumFontWeight;
  color: lighten($blackColor, 35%);
  margin-top: 1rem;

  em {
    color: $blackColor;
    font-weight: $boldFontWeight;
  }
}
</style>
